import * as React from 'react'
import type { Notification as OntologyNotification } from '@axilon-apps/domains-ontology-ui-state'

/** Fired after `requestPermission` settles so watchers can re-read permission. */
export const BROWSER_NOTIFICATION_PERMISSION_EVENT = 'ontology-browser-notification-permission'

/** Whether the browser Notification API is available in this context. */
export function isBrowserNotificationSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window
}

export function getBrowserNotificationPermission(): NotificationPermission | 'unsupported' {
  if (!isBrowserNotificationSupported()) return 'unsupported'
  return Notification.permission
}

function notifyPermissionListeners(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(BROWSER_NOTIFICATION_PERMISSION_EVENT))
}

/**
 * Request permission. Must be called from a user gesture in most browsers.
 * Returns the resulting permission (or `'unsupported'`).
 */
export async function requestBrowserNotificationPermission(): Promise<
  NotificationPermission | 'unsupported'
> {
  if (!isBrowserNotificationSupported()) return 'unsupported'
  try {
    const next = await Notification.requestPermission()
    notifyPermissionListeners()
    return next
  } catch {
    notifyPermissionListeners()
    return Notification.permission
  }
}

function subscribeBrowserNotificationPermission(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined

  const onFocus = () => listener()
  window.addEventListener(BROWSER_NOTIFICATION_PERMISSION_EVENT, listener)
  window.addEventListener('focus', onFocus)
  document.addEventListener('visibilitychange', onFocus)

  return () => {
    window.removeEventListener(BROWSER_NOTIFICATION_PERMISSION_EVENT, listener)
    window.removeEventListener('focus', onFocus)
    document.removeEventListener('visibilitychange', onFocus)
  }
}

/** Live browser Notification.permission (re-reads after grant / tab focus). */
export function useBrowserNotificationPermission(): NotificationPermission | 'unsupported' {
  return React.useSyncExternalStore(
    subscribeBrowserNotificationPermission,
    getBrowserNotificationPermission,
    () => 'unsupported'
  )
}

export interface ShowBrowserNotificationOptions {
  title: string
  body?: string | null
  tag?: string
  onClick?: () => void
}

/**
 * Show a desktop notification. No-ops when unsupported or permission is not
 * granted. Uses `tag` so repeat deliveries for the same id replace rather
 * than stack.
 */
export function showBrowserNotification(options: ShowBrowserNotificationOptions): boolean {
  if (!isBrowserNotificationSupported()) return false
  if (Notification.permission !== 'granted') return false

  try {
    const notification = new Notification(options.title, {
      body: options.body?.trim() ? options.body : undefined,
      tag: options.tag,
      // Stay until dismissed — Windows toasts are easy to miss while focused.
      requireInteraction: true
    })
    if (options.onClick) {
      notification.onclick = () => {
        options.onClick?.()
        notification.close()
      }
    }
    return true
  } catch {
    return false
  }
}

export async function showOntologyBrowserNotification(
  notification: Pick<OntologyNotification, 'id' | 'title' | 'summary'>,
  onClick?: () => void
): Promise<boolean> {
  // Prefer ServiceWorkerRegistration.showNotification — Chrome often
  // suppresses `new Notification()` while the document is hidden.
  try {
    const { showNotificationViaServiceWorker } = await import('./notification-sw-client')
    const viaSw = await showNotificationViaServiceWorker({
      title: notification.title,
      body: notification.summary,
      tag: `ontology-notification-${notification.id}`
    })
    if (viaSw) return true
  } catch {
    // Fall through to the window Notification constructor.
  }
  return showBrowserNotification({
    title: notification.title,
    body: notification.summary,
    tag: `ontology-notification-${notification.id}`,
    onClick
  })
}
